import { CSSProperties, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import cn from 'classnames'
import dayjs from 'dayjs'
import { getContent } from 'api/content'
import { CollapseIcon, ExpandIcon, PlusIcon, SearchIcon } from 'assets/icons'
import Input from 'components/UI/Input'
import SidebarTabs from 'components/Sidebar/SidebarTabs'
import { useOutsideClick } from 'hooks'
import { prepareMessageData, t } from 'helpers'
import { getUser } from 'helpers/storage'
import { propertiesParser } from 'helpers/propertiesParser'
import { CASTING_STATUS, MESSAGE_TYPE, PROJECT_ACCESS } from 'types/enums'
import { User } from 'types/user'
import { Content, SidebarTabsOptions } from 'types/app'
import s from './index.module.scss'
import { useCastingStore, useUserStore } from 'store'
import { formatDate } from 'helpers/dateTimeConfig'
import { useShallow } from 'zustand/react/shallow'

interface Props {
	tabs?: SidebarTabsOptions[]
	type: 'roles' | 'users' | 'chat'
	children: JSX.Element
	users?: User[]
	filter: string
	setFilter: (v: string) => void
	setSearch: (v: string) => void
	style?: CSSProperties
	id?: string
	collapsed: boolean
	setCollapsed: (col: boolean) => void
	notifyFilters?: string[]
}

const ListWrapper = ({
	tabs,
	type,
	children,
	users,
	filter,
	setFilter,
	setSearch,
	style,
	id,
	collapsed,
	setCollapsed,
	notifyFilters,
}: Props): JSX.Element => {
	const navigate = useNavigate()
	const searchRef = useRef(null)
	const { roleId, productionId } = useParams()
	const [showSearch, setShowSearch] = useState(false)
	const [access] = useCastingStore(useShallow((state) => [state.projectAccess]))
	const fullAccess = !access || (access && access === PROJECT_ACCESS.FULLACCESS)

	useOutsideClick(searchRef, () => setShowSearch(false))

	const notifyUsers = async (status: string): Promise<void> => {
		const keys: { [key: string]: string } = {
			[CASTING_STATUS.DISLIKED]: 'NOTIFY_REJECT',
			[CASTING_STATUS.LIKED]: 'NOTIFYUSERS_CALLBACK',
			[CASTING_STATUS.FAVORITE]: 'NOTIFYUSERS_CALLBACK',
		}

		const key = keys[status]

		try {
			const { user } = useUserStore.getState()
			const content = await getContent(roleId || '')
			const role = propertiesParser<Content>(content?.Properties)

			if (users?.length) {
				const data = {
					userIds: users?.map((user) => user?.ContentId),
					sourceId: roleId || '',
					key: key,
					messageValue: t(`APP_${key}_MESSAGE_BODY`)
						?.replace('[YourName]', getUser()?.['Custom:User:Name'] || '')
						.replace('[RoleName]', role?.['Globalizationen-USContentTitle'])
						.replace('[PhoneNumber]', user?.CustomUserPhoneNumber || 'no phone')
						.replace('[Email]', user?.CustomUserEmail || 'no email')
						.replace('[Insert Date]', formatDate(dayjs().toISOString()))
						.replace('[Insert Time]', dayjs().format('hh:mm')),
					redirect: window.location.pathname + window.location.hash,
					controlsProps: {
						prevText: 'APP_BACK',
						nextText: 'APP_SEND_NOTIF',
					},
					messageType: MESSAGE_TYPE.CASTING,
				}
				navigate(prepareMessageData(data), {
					replace: true,
				})
			}
		} catch (error) {
			console.log('error:', error)
		}
	}

	const inviteUsers = async (): Promise<void> => {
		try {
			const prod = await getContent(productionId || '')
			const content = await getContent(roleId || '')
			const production = propertiesParser<Content>(prod?.Properties)
			const role = propertiesParser<Content>(content?.Properties)

			const data = {
				key: 'ROLEINVITE',
				messageValue: t('APP_ROLEINVITE_MESSAGE_BODY')
					?.replace(
						'[RoleName]',
						role?.['Globalizationen-USContentTitle'] || '',
					)
					?.replace(
						'[ProductionTitle]',
						production?.['Globalizationen-USContentTitle'],
					)
					?.replace(
						'[AuditionTitle]',
						role?.['Globalizationen-USContentTitle'] || '',
					)
					?.replace(
						'[AuditionDate]',
						role?.ContentEventDeadline
							? dayjs(role?.ContentEventDeadline || '').format('MM/DD/YYYY')
							: 'N/A',
					)
					?.replace(
						'[AuditionNDA]',
						production?.ContentLegalNDARequired === 'true' ? 'Yes' : 'No',
					)
					?.replace('[YourName]', getUser()?.['Custom:User:Name'] || ''),
				sourceId: role.ContentId,
				redirect: window.location.pathname + window.location.hash,
				openSidebar: true,
				controlsProps: {
					prevText: 'APP_BACK',
					nextText: 'APP_SEND_NOTIF',
				},
				messageType: MESSAGE_TYPE.CASTING,
			}
			navigate(prepareMessageData(data), {
				replace: true,
			})
		} catch (error) {
			console.log('error:', error)
		}
	}

	const fixedHeight =
		type === 'users' && !collapsed
			? 168
			: type === 'roles' || type === 'users'
			? 122
			: type === 'chat' && access !== PROJECT_ACCESS.FULLACCESS
			? 0
			: 206

	let maxHeight = ''
	const wrapper = document.getElementById('casting-overlay-container')
	if (wrapper)
		maxHeight = wrapper.getBoundingClientRect().height - fixedHeight + 'px'
	else maxHeight = '100%'

	return (
		<div className={cn(s.wrapper, { [s.collapse]: collapsed })} style={style}>
			<div
				className={cn(s.header, { [s.showInput]: showSearch })}
				ref={searchRef}>
				<Input
					variant="default"
					name="filterSearch"
					placeholder={t('APP_SEARCH')}
					style={{ padding: '7px 20px' }}
					onChange={(e) => setSearch(e.target.value)}
				/>
				<div className={s.headerWrapper}>
					<div
						onClick={() => setCollapsed(!collapsed)}
						style={collapsed ? { margin: 'auto' } : {}}>
						{collapsed ? (
							<ExpandIcon
								color="var(--mono200)"
								style={type === 'chat' ? { transform: 'rotate(180deg)' } : {}}
							/>
						) : (
							<CollapseIcon
								color="var(--mono200)"
								style={type === 'chat' ? { transform: 'rotate(180deg)' } : {}}
							/>
						)}
					</div>
					{collapsed ? null : (
						<>
							<span className="body1-b">
								{t(
									type === 'users'
										? 'APP_CANDIDATES_LABEL'
										: type === 'roles'
										? 'APP_ROLES'
										: 'APP_CASTING_REVIEW_LABEL',
								)}
							</span>
							<div
								style={{ marginLeft: 'auto' }}
								onClick={() => setShowSearch(true)}>
								<SearchIcon />
							</div>
						</>
					)}
				</div>
			</div>
			{tabs ? (
				<SidebarTabs
					activeClass={s.activeTab}
					active={filter}
					onChange={(v) => {
						if (v === filter) setFilter('')
						else setFilter(v as string)
					}}
					scroll
					options={tabs}
					style={{
						gap: '5px',
					}}
					tabStyle={{
						width: 'fit-content',
						padding: '8px 10px',
						// maxHeight: '46px',
						display: 'flex',
					}}
					tabLabelClassName="body2-s"
				/>
			) : null}
			{type === 'users' && !collapsed && fullAccess ? (
				<div className="grid2column">
					<div
						onClick={inviteUsers}
						style={{
							cursor: 'pointer',
							display: 'flex',
							alignItems: 'center',
							marginBottom: '10px',
							gap: '4px',
						}}>
						<div style={{ display: 'flex' }}>
							<PlusIcon />
						</div>
						<span
							className={cn('body2-m', s.notifyLabel)}
							style={{ marginBottom: '0' }}>
							{t('APP_INVITE_TO_ROLE')}
						</span>
					</div>

					{users?.length && notifyFilters?.includes(filter) ? (
						<span
							onClick={() => notifyUsers(filter)}
							className={cn('body2-m', s.notifyLabel)}
							style={{
								cursor: 'pointer',
								marginLeft: 'auto',
								alignItems: 'center',
							}}>
							{t('APP_NOTIFY_LABEL')}
						</span>
					) : null}
				</div>
			) : null}
			<div
				className={cn(s.listWrapper, 'hideScrollBar')}
				style={{
					maxHeight: maxHeight,
				}}
				id={id}>
				<div className={s.list}>
					{children ? children : <span>{t('APP_RESULT_NOT_FOUND')}</span>}
				</div>
			</div>
		</div>
	)
}

export default ListWrapper
